import {
  collection,
  doc,
  writeBatch,
} from "firebase/firestore";
import { db } from "../firebase";
import { FIREBASE_COLLECTIONS } from "../config/firebaseCollections";
import {
  createTeacherHubEventId,
  normalizeTeacherHubEvent,
  sortTeacherHubEvents,
} from "./teacherHubEvents";
import { readCollectionServerPreferred } from "./firestoreCacheFirstRead";

const BATCH_LIMIT = 450;

const teacherHubEventsCollection = (uid) =>
  collection(db, FIREBASE_COLLECTIONS.USERS, uid, FIREBASE_COLLECTIONS.TEACHER_HUB_EVENTS);

const chunk = (items, size) => {
  const chunks = [];
  for (let index = 0; index < items.length; index += size) {
    chunks.push(items.slice(index, index + size));
  }
  return chunks;
};

const normalizePersistedEvents = (uid, events = [], options = {}) =>
  sortTeacherHubEvents(
    (Array.isArray(events) ? events : []).map((eventItem) => {
      const normalized = normalizeTeacherHubEvent(eventItem, { userId: uid, ...options });
      return {
        ...normalized,
        id: normalized.id || createTeacherHubEventId(),
      };
    })
  );

/** @internal Exported for regression tests. */
export const getTeacherHubEventDeleteDocIds = (existingDocs = [], nextEvents = []) => {
  const nextIds = new Set(
    (Array.isArray(nextEvents) ? nextEvents : [])
      .map((eventItem) => String(eventItem?.id || "").trim())
      .filter(Boolean)
  );

  return (Array.isArray(existingDocs) ? existingDocs : [])
    .filter((eventDoc) => {
      const docId = String(eventDoc?.id || "").trim();
      const dataId = String(eventDoc?.data?.()?.id || "").trim();
      return docId && !nextIds.has(docId) && (!dataId || !nextIds.has(dataId));
    })
    .map((eventDoc) => eventDoc.id);
};

const commitEventUpserts = async (colRef, normalizedEvents) => {
  for (const eventItems of chunk(normalizedEvents, BATCH_LIMIT)) {
    const batch = writeBatch(db);
    eventItems.forEach((eventItem) => {
      batch.set(doc(colRef, eventItem.id), eventItem);
    });
    await batch.commit();
  }
};

const commitEventDeletes = async (colRef, deleteDocIds = []) => {
  for (const idChunk of chunk(deleteDocIds, BATCH_LIMIT)) {
    const batch = writeBatch(db);
    idChunk.forEach((eventDocId) => {
      batch.delete(doc(colRef, eventDocId));
    });
    await batch.commit();
  }
};

export const fetchTeacherHubEvents = async (uid, options = {}) => {
  if (!uid) return [];

  const snapshot = await readCollectionServerPreferred(teacherHubEventsCollection(uid));
  const events = snapshot.docs.map((eventDoc) =>
    normalizeTeacherHubEvent(
      {
        ...eventDoc.data(),
        id: eventDoc.data()?.id || eventDoc.id,
      },
      { userId: uid, ...options }
    )
  );

  return sortTeacherHubEvents(events);
};

export const replaceTeacherHubEvents = async (uid, events = [], options = {}) => {
  if (!uid) {
    throw new Error("Missing user id for teacher hub events.");
  }

  const normalizedEvents = normalizePersistedEvents(uid, events, options);
  const colRef = teacherHubEventsCollection(uid);
  const existingSnapshot = await readCollectionServerPreferred(colRef);
  const deleteDocIds = getTeacherHubEventDeleteDocIds(existingSnapshot.docs, normalizedEvents);

  await commitEventUpserts(colRef, normalizedEvents);
  await commitEventDeletes(colRef, deleteDocIds);

  return normalizedEvents;
};
